import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    ExternalLink,
    Loader2,
    Play,
    RefreshCw,
    Sparkles
} from "lucide-react";
import PageShell from "../components/layout/PageShell";
import {
    deleteDemoShowcaseProjects,
    generateDemoShowcase,
    getDemoShowcaseProjects,
    getDemoShowcaseTemplates
} from "../services/demoShowcaseService";
import "./DemoShowcasePage.css";

const defaultCustomVariants = `{
  "hero": 3,
  "programs": 2,
  "products": 3,
  "trust": 2,
  "cta": 3
}`;

export default function DemoShowcasePage() {
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);

    const [templates, setTemplates] = useState([]);
    const [projects, setProjects] = useState([]);

    const [engine, setEngine] = useState("both");
    const [variantMode, setVariantMode] = useState("manual");
    const [publish, setPublish] = useState(true);
    const [replaceExisting, setReplaceExisting] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [customVariantsJson, setCustomVariantsJson] = useState(defaultCustomVariants);

    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        try {
            setLoading(true);

            const [templateResult, projectResult] = await Promise.all([
                getDemoShowcaseTemplates(),
                getDemoShowcaseProjects()
            ]);

            const demos = templateResult?.demos || [];
            setTemplates(demos);
            setProjects(Array.isArray(projectResult) ? projectResult : []);

            if (selectedKeys.length === 0 && demos.length > 0) {
                setSelectedKeys(demos.map((x) => x.key));
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to load demo showcase.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedTemplates = useMemo(
        () => templates.filter((x) => selectedKeys.includes(x.key)),
        [templates, selectedKeys]
    );

    const toggleDemo = (key) => {
        setSelectedKeys((prev) =>
            prev.includes(key)
                ? prev.filter((x) => x !== key)
                : [...prev, key]
        );
    };

    const selectAll = () => {
        setSelectedKeys(templates.map((x) => x.key));
    };

    const clearAll = () => {
        setSelectedKeys([]);
    };

    const handleDeleteSelected = async () => {
        if (selectedKeys.length === 0) {
            toast.error("Select at least one demo type.");
            return;
        }

        const confirmed = window.confirm(
            `Delete selected ${engine.toUpperCase()} demo projects from database and Azure generated folders?`
        );

        if (!confirmed) return;

        try {
            setDeleting(true);

            const result = await deleteDemoShowcaseProjects({
                demoKeys: selectedKeys,
                engine,
                dryRun: false,
                deleteAzureFiles: true,
                continueOnAzureFailure: true
            });

            if (result?.ok === false) {
                toast.error(result?.message || "Demo cleanup failed.");
            } else {
                toast.success(
                    result?.message ||
                    `Deleted ${result?.projectsDeleted || 0} demo project(s).`
                );
            }

            await load();
        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to delete demo projects."
            );
        } finally {
            setDeleting(false);
        }
    };

    const handleGenerate = async () => {
        if (selectedKeys.length === 0) {
            toast.error("Select at least one demo.");
            return;
        }

        let parsedVariants = null;

        if (variantMode === "custom") {
            try {
                parsedVariants = JSON.parse(customVariantsJson || "{}");
            } catch {
                toast.error("Custom variants JSON is invalid.");
                return;
            }
        }

        try {
            setWorking(true);

            const result = await generateDemoShowcase({
                engine,
                demoKeys: selectedKeys,
                variantMode,
                sectionVariants: parsedVariants,
                customVariantsJson: variantMode === "custom" ? customVariantsJson : null,
                publish,
                replaceExisting
            });

            if (result?.success === false) {
                toast.error(result?.message || "Demo generation completed with errors.");
            } else {
                toast.success(result?.message || "Demo generation started.");
            }

            if (result?.errors?.length) {
                console.warn("Demo showcase errors", result.errors);
            }

            await load();
        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to generate demo showcase."
            );
        } finally {
            setWorking(false);
        }
    };

    return (
        <PageShell title="Demo Showcase">
            <div className="demo-showcase-page">
                <div className="demo-showcase-hero">
                    <div>
                        <span className="demo-kicker">
                            <Sparkles size={15} />
                            Super Admin
                        </span>
                        <h1>Demo Showcase Center</h1>
                        <p>
                            Generate V1 and V2 showcase projects with controlled section variants for sales demos,
                            landing pages, and client presentations.
                        </p>
                    </div>

                    <div className="demo-hero-actions">
                        <button
                            type="button"
                            className="demo-btn secondary"
                            onClick={load}
                            disabled={loading || working}
                        >
                            <RefreshCw size={16} />
                            Refresh
                        </button>

                        <button
                            type="button"
                            className="demo-btn primary"
                            onClick={handleGenerate}
                            disabled={working || loading}
                        >
                            {working ? <Loader2 className="spin" size={16} /> : <Play size={16} />}
                            {working ? "Generating..." : "Generate Showcase"}
                        </button>

                        <button
                            type="button"
                            className="demo-btn secondary danger"
                            onClick={handleDeleteSelected}
                            disabled={deleting || working || loading}
                        >
                            {deleting ? <Loader2 className="spin" size={16} /> : null}
                            {deleting ? "Deleting..." : "Delete Selected"}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="demo-loading">
                        <Loader2 className="spin" size={26} />
                        Loading demo showcase...
                    </div>
                ) : (
                    <>
                        <div className="demo-grid">
                            <section className="demo-panel">
                                <div className="demo-panel-head">
                                    <h2>Generation Settings</h2>
                                    <p>Select engine, demo types, variants and publish behavior.</p>
                                </div>

                                <div className="demo-form-grid">
                                    <label>
                                        Engine
                                        <select value={engine} onChange={(e) => setEngine(e.target.value)}>
                                            <option value="both">V1 + V2</option>
                                            <option value="v1">V1 only</option>
                                            <option value="v2">V2 only</option>
                                        </select>
                                    </label>

                                    <label>
                                        Variant Mode
                                        <select value={variantMode} onChange={(e) => setVariantMode(e.target.value)}>
                                            <option value="manual">Template Defaults</option>
                                            <option value="all-1">All Variant 1</option>
                                            <option value="all-2">All Variant 2</option>
                                            <option value="all-3">All Variant 3</option>
                                            <option value="custom">Custom JSON</option>
                                        </select>
                                    </label>
                                </div>

                                <div className="demo-check-row">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={publish}
                                            onChange={(e) => setPublish(e.target.checked)}
                                        />
                                        Publish / redeploy after create
                                    </label>

                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={replaceExisting}
                                            onChange={(e) => setReplaceExisting(e.target.checked)}
                                        />
                                        Replace existing demo projects
                                    </label>
                                </div>

                                {variantMode === "custom" && (
                                    <label className="demo-json-label">
                                        Custom Section Variants JSON
                                        <textarea
                                            rows={9}
                                            value={customVariantsJson}
                                            onChange={(e) => setCustomVariantsJson(e.target.value)}
                                            spellCheck={false}
                                        />
                                        <small>
                                            Example keys: hero, programs, products, trainers, trust, cta.
                                            Backend will store this for V1 ShellVariantsJson and V2 debugVariants.
                                        </small>
                                    </label>
                                )}
                            </section>

                            <section className="demo-panel">
                                <div className="demo-panel-head with-actions">
                                    <div>
                                        <h2>Demo Types</h2>
                                        <p>Jewellery is V2-only because V1 handles generic ecommerce/fashion better.</p>
                                    </div>

                                    <div className="demo-mini-actions">
                                        <button type="button" onClick={selectAll}>All</button>
                                        <button type="button" onClick={clearAll}>Clear</button>
                                    </div>
                                </div>

                                <div className="demo-template-list">
                                    {templates.map((item) => (
                                        <button
                                            type="button"
                                            key={item.key}
                                            className={`demo-template-card ${selectedKeys.includes(item.key) ? "active" : ""}`}
                                            onClick={() => toggleDemo(item.key)}
                                        >
                                            <div>
                                                <strong>{item.label || item.key}</strong>
                                                <span>{item.key}</span>
                                            </div>

                                            <div className="demo-engine-pills">
                                                {(item.allowedEngines || []).map((x) => (
                                                    <b key={x}>{x}</b>
                                                ))}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </section>
                        </div>

                        <section className="demo-panel">
                            <div className="demo-panel-head">
                                <h2>Selected Showcase Plan</h2>
                                <p>{selectedTemplates.length} demo type(s) selected.</p>
                            </div>

                            <div className="demo-selected-list">
                                {selectedTemplates.map((item) => (
                                    <div className="demo-selected-row" key={item.key}>
                                        <div>
                                            <strong>{item.label}</strong>
                                            <span>
                                                V1: {item.v1BusinessName || "-"} · V2: {item.v2BusinessName || "-"}
                                            </span>
                                        </div>

                                        <code>
                                            {JSON.stringify(item.defaultVariants || {})}
                                        </code>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="demo-panel">
                            <div className="demo-panel-head">
                                <h2>Generated Demo Projects</h2>
                                <p>Open generated sites after worker/redeploy completes.</p>
                            </div>

                            {projects.length === 0 ? (
                                <div className="demo-empty">No demo projects generated yet.</div>
                            ) : (
                                <div className="demo-project-table-wrap">
                                    <table className="demo-project-table">
                                        <thead>
                                            <tr>
                                                <th>Business</th>
                                                <th>Demo</th>
                                                <th>Engine</th>
                                                <th>Status</th>
                                                <th>Subdomain</th>
                                                <th>Variants</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {projects.map((project) => (
                                                <tr key={project.projectId}>
                                                    <td>
                                                        <strong>{project.businessName}</strong>
                                                    </td>
                                                    <td>{project.demoKey}</td>
                                                    <td>
                                                        <span className={`demo-engine ${project.engine}`}>
                                                            {project.engine}
                                                        </span>
                                                    </td>
                                                    <td>{project.status}</td>
                                                    <td>{project.subdomain || "-"}</td>
                                                    <td>
                                                        <code>
                                                            {JSON.stringify(project.sectionVariants || {})}
                                                        </code>
                                                    </td>
                                                    <td>
                                                        {project.liveUrl ? (
                                                            <a
                                                                className="demo-open-link"
                                                                href={project.liveUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                Open
                                                                <ExternalLink size={14} />
                                                            </a>
                                                        ) : project.subdomain ? (
                                                            <a
                                                                className="demo-open-link"
                                                                href={`https://sites.shrianshnexus.in/${project.subdomain}/`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                Open
                                                                <ExternalLink size={14} />
                                                            </a>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </PageShell>
    );
}